import { validateOnlineLifecycle } from "./online-room-lifecycle.js";
import type {
  CertifiedCheckpoint,
  ElectionCertificate,
  ElectionRequest,
  ElectionVote,
  MembershipAcknowledgment,
  MembershipCertificate,
  MembershipProposal,
  RecoveryCheckpoint,
  RecoveryHeartbeat,
  RecoveryStatus,
} from "./recovery-types.js";

export function repeatedElection(
  certificate: ElectionCertificate,
  status: RecoveryStatus,
  certified: readonly CertifiedCheckpoint[],
): CertifiedCheckpoint | undefined {
  if (
    certificate.authorityEpoch !== status.authorityEpoch ||
    certificate.priorAuthorityEpoch !== status.authorityEpoch - 1 ||
    certificate.term !== status.term ||
    certificate.hostMemberId !== status.hostMemberId
  )
    return undefined;
  const installed = certified.find((entry) => entry.checkpoint.id === certificate.checkpointId);
  if (!installed) throw new TypeError("Election certificate names an unavailable checkpoint");
  return structuredClone(installed);
}

export function validateCheckpoint(value: RecoveryCheckpoint): void {
  token(value.id, "checkpoint ID");
  positive(value.authorityEpoch, "checkpoint authority epoch");
  positive(value.membershipVersion, "checkpoint membership version");
  safeTime(value.gameTimeMs, "checkpoint game time");
  if (!/^(?:0|[1-9][0-9]*)$/.test(value.revision))
    throw new TypeError("Invalid checkpoint revision");
  token(value.hostMemberId, "checkpoint host member ID");
  voterSet(value.voters);
  if (
    !Array.isArray(value.roster) ||
    !Array.isArray(value.bots) ||
    typeof value.save !== "string" ||
    value.save.length < 1
  )
    throw new TypeError("Invalid recovery checkpoint payload");
  for (const entry of value.roster) {
    token(entry.sessionId, "checkpoint session ID");
    if (!["host", "controller", "spectator"].includes(entry.role))
      throw new TypeError("Invalid checkpoint roster role");
    if (entry.seatId !== null) token(entry.seatId, "checkpoint seat ID");
    if (entry.reconnectVerifier !== undefined && !/^[0-9a-f]{64}$/.test(entry.reconnectVerifier))
      throw new TypeError("Invalid checkpoint reconnect verifier");
  }
  if (new Set(value.roster.map((entry) => entry.sessionId)).size !== value.roster.length)
    throw new TypeError("Duplicate checkpoint session");
  const assignedSeats = value.roster.flatMap((entry) => (entry.seatId ? [entry.seatId] : []));
  if (new Set(assignedSeats).size !== assignedSeats.length)
    throw new TypeError("Duplicate checkpoint seat assignment");
  if (!value.roster.some((entry) => entry.sessionId === value.hostMemberId))
    throw new TypeError("Checkpoint host is absent from its roster");
  const botSeats = new Set<string>();
  for (const bot of value.bots) {
    token(bot.seatId, "checkpoint bot seat ID");
    if (
      botSeats.has(bot.seatId) ||
      !Number.isSafeInteger(bot.sequence) ||
      bot.sequence < 0 ||
      !Number.isSafeInteger(bot.nextDecisionAtMs) ||
      bot.nextDecisionAtMs < 0 ||
      !["builder", "defender", "raider", "siege-planner", "broker"].includes(bot.personality) ||
      !["apprentice", "foreman", "superintendent"].includes(bot.difficulty) ||
      !bot.memory ||
      !Number.isSafeInteger(bot.memory.decision) ||
      bot.memory.decision < 0 ||
      !Number.isSafeInteger(bot.memory.seedCursor) ||
      bot.memory.seedCursor < 0 ||
      !Number.isSafeInteger(bot.memory.diplomacyCooldownUntilMs) ||
      bot.memory.diplomacyCooldownUntilMs < 0 ||
      (bot.memory.lastTargetId !== null && typeof bot.memory.lastTargetId !== "string") ||
      (bot.memory.lastAction !== null && typeof bot.memory.lastAction !== "string")
    )
      throw new TypeError("Invalid checkpoint bot state");
    botSeats.add(bot.seatId);
  }
  if (value.lifecycle) validateOnlineLifecycle(value.lifecycle);
}

export function validateCertified(value: CertifiedCheckpoint, voters: readonly string[]): void {
  validateCheckpoint(value.checkpoint);
  const certificate = value.certificate;
  if (
    certificate.checkpointId !== value.checkpoint.id ||
    certificate.authorityEpoch !== value.checkpoint.authorityEpoch ||
    certificate.membershipVersion !== value.checkpoint.membershipVersion
  )
    throw new TypeError("Checkpoint certificate does not name its checkpoint");
  const acknowledgers = voterSubset(certificate.acknowledgers, voters);
  if (
    !same(acknowledgers, certificate.acknowledgers) ||
    !majority(acknowledgers.length, voters.length) ||
    !acknowledgers.includes(value.checkpoint.hostMemberId)
  )
    throw new TypeError(
      "Checkpoint certificate lacks its host and a unique current-voter majority",
    );
}

export function validateHeartbeat(value: RecoveryHeartbeat): void {
  positive(value.authorityEpoch, "heartbeat authority epoch");
  safeTime(value.term, "heartbeat term");
  safeTime(value.gameTimeMs, "heartbeat game time");
  token(value.hostMemberId, "heartbeat host member ID");
}

export function validateElectionRequest(value: ElectionRequest): void {
  positive(value.term, "election term");
  positive(value.priorAuthorityEpoch, "election authority epoch");
  token(value.candidateId, "election candidate ID");
  token(value.checkpointId, "election checkpoint ID");
  voterSet(value.connectedVoters);
}

export function validateElectionVote(value: ElectionVote): void {
  positive(value.term, "vote term");
  positive(value.priorAuthorityEpoch, "vote authority epoch");
  token(value.candidateId, "vote candidate ID");
  token(value.checkpointId, "vote checkpoint ID");
  token(value.voterId, "vote member ID");
}

export function validateElectionCertificate(
  value: ElectionCertificate,
  voters: readonly string[],
): void {
  positive(value.term, "election certificate term");
  positive(value.priorAuthorityEpoch, "prior authority epoch");
  positive(value.authorityEpoch, "new authority epoch");
  token(value.hostMemberId, "elected host member ID");
  token(value.checkpointId, "elected checkpoint ID");
  const unique = voterSubset(value.voters, voters);
  if (
    !same(unique, value.voters) ||
    !majority(unique.length, voters.length) ||
    !voters.includes(value.hostMemberId)
  )
    throw new TypeError("Election certificate lacks a unique current-voter majority");
}

export function validateMembershipProposal(value: MembershipProposal): void {
  positive(value.membershipVersion, "membership version");
  positive(value.authorityEpoch, "membership authority epoch");
  token(value.proposedBy, "membership proposer");
  voterSet(value.voters);
}

export function validateMembershipAcknowledgment(value: MembershipAcknowledgment): void {
  validateMembershipProposal(value);
  token(value.memberId, "membership acknowledger");
}

export function validateMembershipCertificate(
  value: MembershipCertificate,
  oldVoters: readonly string[],
): void {
  validateMembershipProposal(value);
  const acknowledgers = voterSubset(value.acknowledgers, oldVoters);
  if (
    !same(acknowledgers, value.acknowledgers) ||
    !majority(acknowledgers.length, oldVoters.length)
  )
    throw new TypeError("Membership certificate lacks an old-voter majority");
}

export function orderCheckpoint(left: RecoveryCheckpoint, right: RecoveryCheckpoint): number {
  const revisions = BigInt(left.revision) - BigInt(right.revision);
  return (
    left.gameTimeMs - right.gameTimeMs ||
    (revisions < 0n ? -1 : revisions > 0n ? 1 : 0) ||
    left.id.localeCompare(right.id)
  );
}

export function electionKey(value: ElectionRequest): string {
  return `${value.priorAuthorityEpoch}:${value.candidateId}:${value.checkpointId}:${value.connectedVoters.join(",")}`;
}

export function membershipKey(value: MembershipAcknowledgment): string {
  return `${value.authorityEpoch}:${value.membershipVersion}:${value.proposedBy}:${value.voters.join(",")}`;
}

export function voterSet(values: readonly string[]): readonly string[] {
  if (!Array.isArray(values) || values.length < 1)
    throw new TypeError("Recovery voter set is empty");
  const normalized = [...new Set(values.map((value) => token(value, "recovery voter ID")))].sort();
  if (normalized.length !== values.length)
    throw new TypeError("Recovery voter set contains duplicates");
  return Object.freeze(normalized);
}

export function voterSubset(
  values: readonly string[],
  electorate: readonly string[],
): readonly string[] {
  const normalized = voterSet(values);
  if (normalized.some((value) => !electorate.includes(value)))
    throw new TypeError("Recovery certificate contains a non-voter");
  return normalized;
}

export function majority(count: number, total: number): boolean {
  return count > total / 2;
}

export function same(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

export function token(value: unknown, label: string): string {
  if (typeof value !== "string" || value.length < 1 || value.length > 128)
    throw new TypeError(`Invalid ${label}`);
  return value;
}

export function positive(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 1)
    throw new TypeError(`Invalid ${label}`);
  return value as number;
}

export function safeTime(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0)
    throw new TypeError(`Invalid ${label}`);
  return value as number;
}

export function trimRecoveryMap<Value>(values: Map<unknown, Value>, maximum: number): void {
  while (values.size > maximum) {
    const oldest = values.keys().next().value;
    if (oldest === undefined) return;
    values.delete(oldest);
  }
}
